// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import MyListsEditListForm from "@insite/content-library/Widgets/MyLists/MyListsEditListForm";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@turnerBlueprint/App/Store/Selectors/GeneralSelectors";
import Button from "@turnerBlueprint/MobiusOverrides/Button/Button";
import { XNGAGE_CUSTOM_WIDGET, sharedDateTimeOptions } from "@turnerBlueprint/XDL/constants";
import React, { FC, useEffect, useState } from "react";
import { ResolveThunks, connect } from "react-redux";

enum fields {
    title = "title",
    buttonLink = "buttonLink",
    buttonTitle = "buttonTitle",
}

interface MyListsOverviewStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    body?: InjectableCss;
    createListModal?: ModalPresentationProps;
    listImage?: LazyImageProps;
    listCard?: InjectableCss;
    infoContainer?: InjectableCss;
    listTitle?: LinkPresentationProps;
    listInfoText?: TypographyPresentationProps;
    centeredContent?: InjectableCss;
}

export const myListsOverviewStyles: MyListsOverviewStyles = {};

const styles = myListsOverviewStyles;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonTitle]: string;
    };
}

const OverViewParameters = {
    page: 1,
    pageSize: 3,
    sort: "ModifiedOn DESC",
    expand: ["top3products"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        myListsPage: getPageLinkByPageType(state, "MyListsPage")?.url,
        myListsDetailsPage: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
        wishListsDataView: getWishListsDataView(state, OverViewParameters),
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadWishLists,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyListsOverview: FC<Props> = ({
    fields: { title, buttonLink, buttonTitle },
    wishListsDataView,
    myListsPage,
    loadWishLists,
    isAuthenticated,
    language,
    myListsDetailsPage,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const linkUrl = useGetLink(buttonLink).url;
    useEffect(() => {
        if (!wishListsDataView.isLoading && !wishListsDataView.value) {
            loadWishLists(OverViewParameters);
        }
    }, []);
    if (!isAuthenticated) {
        return null;
    }

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.heading}>
                <Typography {...styles.title}>{title || translate("My Lists")}</Typography>
                <Link href={linkUrl || myListsPage} {...styles.link}>
                    {buttonTitle || "View All"}
                </Link>
            </StyledWrapper>
            <StyledWrapper {...styles.body}>
                {wishListsDataView.isLoading && (
                    <StyledWrapper {...styles.centeredContent}>
                        <LoadingSpinner />
                    </StyledWrapper>
                )}
                {!wishListsDataView.isLoading && wishListsDataView.value?.length === 0 ? (
                    <Button
                        onClick={openModal}
                        aria-label="overViewCreateList"
                        data-test-selector="overview-create-list"
                    >
                        {translate("Create Your First List")}
                    </Button>
                ) : (
                    wishListsDataView.value?.map(list => {
                        const date = getLocalizedDateTime({
                            dateTime: new Date(list.updatedOn),
                            language,
                            options: {
                                ...sharedDateTimeOptions,
                            },
                        });
                        return (
                            <StyledWrapper {...styles.listCard} key={list.id}>
                                <LazyImage
                                    src={list.wishListLineCollection?.[0]?.smallImagePath}
                                    altText={list.description}
                                    {...styles.listImage}
                                />
                                <StyledWrapper {...styles.infoContainer}>
                                    <Link
                                        href={`${myListsDetailsPage}?id=${list.id}`}
                                        {...styles.listTitle}
                                        data-test-selector="list-name-link"
                                    >
                                        {list.name}
                                    </Link>
                                    <Typography {...styles.listInfoText}>{`${translate("Updated")} ${date} ${translate(
                                        "by",
                                    )} ${list.updatedByDisplayName}`}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })
                )}
            </StyledWrapper>
            <Modal
                headline={translate("Create List")}
                {...styles.createListModal}
                isOpen={isModalOpen}
                handleClose={closeModal}
            >
                <MyListsEditListForm onCancel={closeModal} onSubmit={closeModal} />
            </Modal>
        </StyledWrapper>
    );
};
const MyListsOverviewWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsOverview),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                editorTemplate: "TextField",
                name: fields.title,
                fieldType: "General",
                defaultValue: "My Lists",
            },
            {
                editorTemplate: "TextField",
                name: fields.buttonTitle,
                fieldType: "General",
                defaultValue: "View All",
            },
            {
                editorTemplate: "LinkField",
                name: fields.buttonLink,
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};
export default MyListsOverviewWidget;
